// Adding or editing one subcontractor insurance (round 41).
//
// RecordModal's two half-right rules carry over:
//
//   1. The RECORD saves first, then the certificate uploads. If the upload
//      fails the record is still there and the message says to add the file
//      from the Documents panel — the alternative is throwing away a
//      correctly-entered policy because the connection dropped mid-scan.
//
//   2. The expiry is REQUIRED, unlike a competency's — every certificate of
//      currency prints its period of insurance, and a record without the date
//      would sit on the page looking "on file" while never reaching the
//      reminders. No pre-fill here, though: policies have no validity list to
//      suggest from, and the date is on the certificate in the person's hand.
import { useState } from 'react';
import { ApiError } from '../../api';
import { uploadAttachment } from '../../documentsApi';
import {
  INSURANCE_TYPES,
  INSURANCE_TYPE_LABEL,
  insurances,
  type InsuranceRecord,
  type InsuranceType,
} from '../../insuranceApi';
import { Modal, SingleFileDrop } from '../ui';
import { Banner, Field } from '../../ui';

export function InsuranceModal({
  resourceId,
  record,
  onClose,
  onSaved,
}: {
  resourceId: string;
  /** Null when adding. */
  record: InsuranceRecord | null;
  onClose: () => void;
  onSaved: (message: string) => void;
}) {
  const [insuranceType, setInsuranceType] = useState<InsuranceType>(
    record?.insuranceType ?? 'public_liability'
  );
  const [otherLabel, setOtherLabel] = useState(record?.otherLabel ?? '');
  const [insurer, setInsurer] = useState(record?.insurer ?? '');
  const [policyNumber, setPolicyNumber] = useState(record?.policyNumber ?? '');
  const [issueDate, setIssueDate] = useState(record?.issueDate ?? '');
  const [expiryDate, setExpiryDate] = useState(record?.expiresOn ?? '');
  const [notes, setNotes] = useState(record?.notes ?? '');
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [fields, setFields] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);

  async function save(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    setFields({});

    const body = {
      insuranceType,
      // Sent only when it means something; on the named types the server
      // refuses a label, so an old value must not ride along after a type
      // change in this dialog.
      otherLabel: insuranceType === 'other' ? otherLabel.trim() || null : null,
      insurer: insurer.trim() || null,
      policyNumber: policyNumber.trim() || null,
      issueDate: issueDate || null,
      expiryDate: expiryDate || null,
      notes: notes.trim() || null,
    };

    try {
      const saved = record
        ? await insurances.update(resourceId, record.id, body)
        : await insurances.add(resourceId, body);

      // The record is safe from here on. A failed upload below costs the
      // file and says so; it never costs the record.
      if (file) {
        try {
          await uploadAttachment({
            kind: 'resource',
            ownerId: resourceId,
            file,
            insuranceRecordId: saved.record.id,
          });
        } catch (err) {
          onSaved(
            `${saved.record.title} was saved, but the certificate did not upload; ` +
              `${err instanceof ApiError ? err.message : 'the upload failed'}. ` +
              'Add it from the Documents panel to try again.'
          );
          return;
        }
      }

      onSaved(record ? 'Saved.' : `${saved.record.title} added.`);
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
        setFields(err.fields);
      } else {
        setError('Could not save that.');
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      title={record ? `Edit ${record.title}` : 'Add insurance'}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" form="insurance-form" className="btn-primary" disabled={busy}>
            {busy ? 'Saving…' : 'Save'}
          </button>
        </>
      }
    >
      <form id="insurance-form" onSubmit={save} noValidate>
        {error && <Banner kind="error">{error}</Banner>}

        <label htmlFor="insurance-type" className="block">
          Insurance
          <select
            id="insurance-type"
            className="form-input"
            value={insuranceType}
            onChange={(e) => setInsuranceType(e.target.value as InsuranceType)}
          >
            {INSURANCE_TYPES.map((type) => (
              <option key={type} value={type}>
                {INSURANCE_TYPE_LABEL[type]}
              </option>
            ))}
          </select>
          {fields.insuranceType && <span className="field-error">{fields.insuranceType}</span>}
        </label>

        {insuranceType === 'other' && (
          <Field
            label="What kind"
            name="otherLabel"
            value={otherLabel}
            onChange={setOtherLabel}
            error={fields.otherLabel}
            hint="“Contract works”, “Plant & equipment”…"
          />
        )}

        <Field
          label="Insurer"
          name="insurer"
          value={insurer}
          onChange={setInsurer}
          error={fields.insurer}
          required={false}
          hint="As named on the certificate of currency."
        />

        <Field
          label="Policy number"
          name="policyNumber"
          value={policyNumber}
          onChange={setPolicyNumber}
          error={fields.policyNumber}
          required={false}
          autoCapitalize="none"
          spellCheck={false}
        />

        {/* `pair`: the two dates stay side by side on a phone instead of
            wrapping into a cramped stack (legacy.css has the argument). */}
        <div className="row pair">
          <Field
            label="Issued"
            name="issueDate"
            type="date"
            value={issueDate}
            onChange={setIssueDate}
            error={fields.issueDate}
            required={false}
          />
          <Field
            label="Expires"
            name="expiryDate"
            type="date"
            value={expiryDate}
            onChange={setExpiryDate}
            error={fields.expiryDate}
          />
        </div>
        {/* Under the row, never on the Expires field — a hint inside a `.row`
            knocks its own field out of line (round 34). */}
        <p className="hint -mt-1">
          Both dates are on the certificate. The expiry is what the reminders watch.
        </p>

        <label htmlFor="insurance-notes" className="block">
          Notes
          <textarea
            id="insurance-notes"
            className="form-input"
            rows={2}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
          <span className="hint">
            Cover amount, excess, anything the certificate says worth keeping.
          </span>
        </label>

        <div>
          <span className="mb-1 block">Certificate of currency</span>
          {/* Round 45: a drop zone as well as a browse, through the shared
              FileDrop (its hidden input keeps round 33's rule). */}
          <SingleFileDrop
            id="insurance-file"
            file={file}
            onChange={setFile}
            accept=".pdf,.jpg,.jpeg,.png,.gif,.webp,.heic"
            typeLabel="PDF or image"
            onReject={setError}
          />
          <span className="hint">
            {record
              ? 'Optional: adds another certificate. The ones already attached are listed on the record.'
              : 'Optional: you can attach one later instead. The record holds the expiry date, so the file does not need its own.'}
          </span>
        </div>
      </form>
    </Modal>
  );
}
